// Program detail page — renders content based on page_type.
import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { api } from "../../shared/api/client";
import { I } from "../../shared/Icon";
import { toast } from "../../shared/stores/toasts";
import { useAuth } from "../../shared/stores/auth";
import { pageTypeIcon, pageTypeLabel } from "../../shell/ProgramsSidebar";
import CustomAppView from "./CustomAppView";
import EmbedView from "./EmbedView";

// ── Types ─────────────────────────────────────────────────────────────────────

type Service = {
  id: number; name: string; description: string | null;
  price: number; duration_days: number; tags: string | null;
  is_active: boolean; program_id: number;
};
type Program = {
  id: number; name: string; category: string; description: string | null;
  is_active: boolean; page_type: string; uuid: string | null; folder_id: number | null;
  services: Service[];
};
type Folder = { id: number; name: string };

// ── Helpers ───────────────────────────────────────────────────────────────────

function isAdmin(user: any): boolean {
  const roles: string[] = user?.roles ?? user?.role_codes ?? [];
  return roles.includes("SUPER_ADMIN") || roles.includes("ADMIN");
}
function fmtPrice(n: number) { return n === 0 ? "무료" : "₩" + n.toLocaleString(); }

const TAG_COLOR: Record<string, string> = { 추천: "#16A34A", 신규: "#2563EB", 필수: "#DC2626" };
function TagPill({ tag }: { tag: string }) {
  const color = TAG_COLOR[tag] ?? "#6B7280";
  return (
    <span style={{ padding: "1px 7px", borderRadius: 10, fontSize: 10, fontWeight: 700, background: `${color}18`, color, border: `1px solid ${color}40` }}>
      {tag}
    </span>
  );
}

const smBtn: React.CSSProperties = {
  fontSize: 11, padding: "3px 10px", borderRadius: 5, border: "1px solid var(--border)",
  background: "none", color: "var(--text-2)", cursor: "pointer",
};

// ── Admin settings panel ──────────────────────────────────────────────────────

type SettingsForm = { name: string; description: string; page_type: string; folder_id: string; is_active: boolean };

function AdminSettings({ program, folders, onUpdate, onDelete }: {
  program: Program; folders: Folder[];
  onUpdate: (p: Program) => void; onDelete: () => void;
}) {
  const [form, setForm] = useState<SettingsForm>({
    name: program.name,
    description: program.description ?? "",
    page_type: program.page_type,
    folder_id: program.folder_id != null ? String(program.folder_id) : "",
    is_active: program.is_active,
  });
  const [saving, setSaving] = useState(false);
  const dirty = form.name !== program.name
    || form.description !== (program.description ?? "")
    || form.page_type !== program.page_type
    || form.folder_id !== (program.folder_id != null ? String(program.folder_id) : "")
    || form.is_active !== program.is_active;

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const res = await api.patch<Program>(`/programs/${program.id}`, {
        name: form.name.trim(),
        description: form.description.trim() || null,
        page_type: form.page_type,
        folder_id: form.folder_id ? parseInt(form.folder_id) : null,
        is_active: form.is_active,
      });
      onUpdate(res.data);
      toast.success("저장됨");
    } catch (e: any) { toast.error("저장 실패", e?.response?.data?.error?.message ?? ""); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ borderBottom: "1px solid var(--border)", background: "var(--bg-1)", padding: "14px 24px" }}>
      <div style={{ maxWidth: 800, display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 12, alignItems: "end" }}>
        {/* Name */}
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-3)", display: "block", marginBottom: 4 }}>이름</label>
          <input className="t-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={{ fontSize: 13 }} />
        </div>

        {/* Description */}
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-3)", display: "block", marginBottom: 4 }}>설명</label>
          <input className="t-input" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="(선택)" style={{ fontSize: 13 }} />
        </div>

        {/* Save / Delete */}
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <button className="t-btn t-btn-primary" onClick={save} disabled={saving || !dirty} style={{ whiteSpace: "nowrap" }}>
            {saving ? "저장 중…" : "저장"}
          </button>
          <button onClick={() => { if (confirm("프로그램을 삭제하시겠습니까?")) onDelete(); }}
            style={{ ...smBtn, color: "#ef4444", borderColor: "#ef4444" }}>삭제</button>
        </div>
      </div>

      {/* Second row: folder + is_active */}
      <div style={{ maxWidth: 800, marginTop: 10, display: "grid", gridTemplateColumns: "auto 1fr", gap: 12, alignItems: "center" }}>
        {/* Folder */}
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-3)", display: "block", marginBottom: 4 }}>폴더</label>
          <select className="t-input" value={form.folder_id} onChange={e => setForm(f => ({ ...f, folder_id: e.target.value }))} style={{ fontSize: 12 }}>
            <option value="">폴더 없음</option>
            {folders.map(fl => <option key={fl.id} value={fl.id}>{fl.name}</option>)}
          </select>
        </div>

        {/* is_active */}
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-3)", display: "block", marginBottom: 4 }}>상태</label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 12 }}>
            <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
            활성화
          </label>
        </div>
      </div>

      {/* Read-only UUID / link / folder (immutable after creation) */}
      {program.uuid && (
        <div style={{ maxWidth: 800, marginTop: 12, padding: "8px 12px", borderRadius: 8, background: "var(--bg-2)", display: "flex", flexWrap: "wrap", gap: "4px 18px", fontSize: 11, color: "var(--text-3)", fontFamily: "ui-monospace, monospace" }}>
          <span>🆔 <b style={{ color: "var(--text-2)" }}>{program.uuid}</b></span>
          <span>🔗 /programs/{program.uuid}</span>
          <span>📂 data/programs/{program.uuid}</span>
        </div>
      )}
    </div>
  );
}

// ── Service modal ─────────────────────────────────────────────────────────────

type SvcForm = { name: string; description: string; price: string; duration_days: string; tags: string };

function ServiceModal({ initial, programId, onClose, onSaved }: {
  initial?: Service; programId: number;
  onClose: () => void; onSaved: (s: Service) => void;
}) {
  const [form, setForm] = useState<SvcForm>({
    name: initial?.name ?? "", description: initial?.description ?? "",
    price: String(initial?.price ?? 0), duration_days: String(initial?.duration_days ?? 1),
    tags: initial?.tags ?? "",
  });
  const [saving, setSaving] = useState(false);
  const isNew = !initial;

  const submit = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const body = { name: form.name.trim(), description: form.description || null, price: parseInt(form.price) || 0, duration_days: parseInt(form.duration_days) || 1, tags: form.tags.trim() || null };
      const res = isNew
        ? await api.post<Service>(`/programs/${programId}/services`, body)
        : await api.patch<Service>(`/programs/${programId}/services/${initial!.id}`, body);
      onSaved(res.data);
      onClose();
    } catch (e: any) { toast.error("저장 실패", e?.response?.data?.error?.message ?? ""); }
    finally { setSaving(false); }
  };

  return (
    <div className="t-overlay" onClick={onClose}>
      <div className="t-cmdk" onClick={e => e.stopPropagation()} style={{ width: 480, maxWidth: "95vw" }}>
        <div style={{ padding: "14px 16px 10px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 8 }}>
          <h3 style={{ margin: 0, fontSize: 14, flex: 1 }}>{isNew ? "새 서비스" : "서비스 수정"}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "var(--text-3)" }}>✕</button>
        </div>
        <div style={{ padding: 16, display: "grid", gap: 12 }}>
          <div>
            <label className="t-label">서비스명 *</label>
            <input className="t-input" autoFocus value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} onKeyDown={e => e.key === "Enter" && submit()} />
          </div>
          <div>
            <label className="t-label">설명</label>
            <textarea className="t-input" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} style={{ minHeight: 60, resize: "vertical", fontFamily: "inherit" }} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label className="t-label">가격 (원)</label>
              <input className="t-input" type="number" min={0} value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} />
            </div>
            <div>
              <label className="t-label">기간 (일)</label>
              <input className="t-input" type="number" min={1} value={form.duration_days} onChange={e => setForm(f => ({ ...f, duration_days: e.target.value }))} />
            </div>
          </div>
          <div>
            <label className="t-label">태그 (쉼표 구분: 추천,신규,필수)</label>
            <input className="t-input" value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} placeholder="추천,필수" />
          </div>
        </div>
        <div style={{ padding: "10px 16px", borderTop: "1px solid var(--border)", display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="t-btn" onClick={onClose} disabled={saving}>취소</button>
          <button className="t-btn t-btn-primary" onClick={submit} disabled={saving || !form.name.trim()}>{saving ? "저장 중…" : isNew ? "추가" : "저장"}</button>
        </div>
      </div>
    </div>
  );
}

// ── Service card ──────────────────────────────────────────────────────────────

function ServiceCard({ svc, admin, onEdit, onDelete, onBook, booking }: {
  svc: Service; admin: boolean; programId: number;
  onEdit: () => void; onDelete: () => void;
  onBook: () => void; booking: boolean;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ border: "1px solid var(--border)", borderRadius: 10, padding: 14, display: "flex", flexDirection: "column", gap: 8, background: "var(--bg-0)", transition: "box-shadow .15s", boxShadow: hovered ? "0 2px 10px rgba(0,0,0,0.08)" : "none" }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
        <span style={{ flex: 1, fontWeight: 600, fontSize: 13, color: "var(--text-1)", lineHeight: 1.4 }}>{svc.name}</span>
        {svc.tags && svc.tags.split(",").map(t => <TagPill key={t} tag={t.trim()} />)}
        {!svc.is_active && admin && <span style={{ fontSize: 10, color: "#9CA3AF", border: "1px solid #9CA3AF", padding: "1px 6px", borderRadius: 8 }}>비활성</span>}
      </div>
      {svc.description && <p style={{ margin: 0, fontSize: 12, color: "var(--text-3)", lineHeight: 1.6 }}>{svc.description}</p>}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "auto", paddingTop: 4, borderTop: "1px solid var(--border)" }}>
        <div>
          <span style={{ fontSize: 14, fontWeight: 700, color: "var(--text-1)" }}>{fmtPrice(svc.price)}</span>
          <span style={{ fontSize: 11, color: "var(--text-3)", marginLeft: 6 }}>{svc.duration_days}일</span>
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          {admin && (
            <>
              <button onClick={onEdit} style={smBtn}>수정</button>
              <button onClick={onDelete} style={{ ...smBtn, color: "#ef4444", borderColor: "#ef4444" }}>삭제</button>
            </>
          )}
          {svc.is_active && (
            <button onClick={onBook} disabled={booking}
              style={{ ...smBtn, background: "var(--p-500,#6366f1)", color: "#fff", border: "none", padding: "4px 14px", fontWeight: 600 }}>
              {booking ? "신청 중…" : "예약 신청"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Catalog page type ─────────────────────────────────────────────────────────

function CatalogView({ program, admin, onUpdate }: {
  program: Program; admin: boolean; onUpdate: (p: Program) => void;
}) {
  const [addSvc,    setAddSvc]    = useState(false);
  const [editSvc,   setEditSvc]   = useState<Service | null>(null);
  const [bookingId, setBookingId] = useState<number | null>(null);

  const services = program.services.filter(s => s.is_active || admin);

  const book = async (serviceId: number) => {
    setBookingId(serviceId);
    try {
      await api.post("/programs/bookings", { service_id: serviceId });
      toast.success("예약 신청됨", "관리자가 확인 후 안내드립니다.");
    } catch (e: any) { toast.error("예약 실패", e?.response?.data?.error?.message ?? ""); }
    finally { setBookingId(null); }
  };

  const deleteSvc = async (svc: Service) => {
    if (!confirm(`"${svc.name}" 서비스를 삭제하시겠습니까?`)) return;
    try {
      await api.delete(`/programs/${program.id}/services/${svc.id}`);
      onUpdate({ ...program, services: program.services.filter(s => s.id !== svc.id) });
      toast.success("삭제됨");
    } catch { toast.error("삭제 실패"); }
  };

  return (
    <div>
      {admin && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
          <button className="t-btn t-btn-primary" onClick={() => setAddSvc(true)}>
            <I.Plus size={13}/> 새 서비스
          </button>
        </div>
      )}

      {services.length === 0 && (
        <div style={{ padding: "60px 0", textAlign: "center", color: "var(--text-3)" }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>📦</div>
          <div style={{ fontSize: 14, fontWeight: 500 }}>서비스가 없습니다</div>
          {admin && <div style={{ fontSize: 12, marginTop: 4 }}>위 버튼으로 서비스를 추가하세요.</div>}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
        {services.map(svc => (
          <ServiceCard
            key={svc.id} svc={svc} admin={admin} programId={program.id}
            onEdit={() => setEditSvc(svc)}
            onDelete={() => deleteSvc(svc)}
            onBook={() => book(svc.id)}
            booking={bookingId === svc.id}
          />
        ))}
      </div>

      {addSvc && (
        <ServiceModal
          programId={program.id} onClose={() => setAddSvc(false)}
          onSaved={svc => { onUpdate({ ...program, services: [...program.services, svc] }); setAddSvc(false); }}
        />
      )}
      {editSvc && (
        <ServiceModal
          initial={editSvc} programId={program.id} onClose={() => setEditSvc(null)}
          onSaved={updated => { onUpdate({ ...program, services: program.services.map(s => s.id === updated.id ? updated : s) }); setEditSvc(null); }}
        />
      )}
    </div>
  );
}

// ── Coming soon (placeholder for future page types) ───────────────────────────

const COMING_SOON: Record<string, { desc: string; preview: string }> = {
  booking:   { desc: "예약 달력 — 날짜/시간 선택 후 예약할 수 있는 캘린더 기반 예약 시스템", preview: "📅" },
  portfolio: { desc: "포트폴리오 — 이미지/영상/파일을 갤러리 형태로 전시하는 쇼케이스 페이지", preview: "🖼️" },
  board:     { desc: "게시판 — 공지사항, Q&A, 피드백 등을 작성하고 관리하는 게시판", preview: "📌" },
  form:      { desc: "신청 폼 — 드래그&드롭으로 설문지나 신청서를 만들고 결과를 수집", preview: "📝" },
  landing:   { desc: "소개 페이지 — 서비스/프로그램을 홍보하는 랜딩 페이지 빌더", preview: "🌐" },
};

function ComingSoon({ pageType }: { pageType: string }) {
  const info = COMING_SOON[pageType];
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "80px 24px", textAlign: "center" }}>
      <div style={{ fontSize: 64, marginBottom: 20 }}>{info?.preview ?? "🚧"}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: "var(--text-1)", marginBottom: 8 }}>
        {pageTypeLabel(pageType)} — 개발 예정
      </div>
      <p style={{ fontSize: 13, color: "var(--text-3)", maxWidth: 480, lineHeight: 1.7, margin: 0 }}>
        {info?.desc ?? `${pageTypeLabel(pageType)} 기능이 곧 추가될 예정입니다.`}
      </p>
      <div style={{ marginTop: 24, padding: "8px 16px", borderRadius: 20, background: "var(--bg-2)", fontSize: 12, color: "var(--text-3)", border: "1px dashed var(--border)" }}>
        🔨 이 페이지 유형은 현재 구현 중입니다
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ProgramDetailPage() {
  const { id: ident } = useParams<{ id: string }>();
  const nav = useNavigate();
  const loc = useLocation();
  const { user } = useAuth();
  const admin = isAdmin(user);
  // "크게 보기" mode — opened in a new window showing only this program.
  const params = new URLSearchParams(loc.search);
  const bare = params.get("bare") === "1";
  // 모바일 앱 껍데기 안. 좁은 화면에서 프로그램 이름줄까지 자리를 먹으면 아까우므로
  // 머리줄과 설명을 아예 걷어내고 프로그램만 남긴다.
  const inApp = params.get("app") === "1";
  const openLarge = () => window.open(`/programs/${ident}?bare=1`, "_blank", "noopener");

  const [program,   setProgram]   = useState<Program | null>(null);
  const [folders,   setFolders]   = useState<Folder[]>([]);
  const [showAdmin, setShowAdmin] = useState(false);
  const [loading,   setLoading]   = useState(true);
  const [notFound,  setNotFound]  = useState(false);

  useEffect(() => {
    if (!ident) return;
    setLoading(true);
    setNotFound(false);
    // The link address may be a numeric id or the program's UUID / slug.
    const progUrl = /^\d+$/.test(ident)
      ? `/programs/${ident}`
      : `/programs/by-uuid/${encodeURIComponent(ident)}`;
    Promise.all([
      api.get<Program>(progUrl),
      admin ? api.get<any[]>("/programs/folders") : Promise.resolve({ data: [] }),
    ])
      .then(([progRes, foldersRes]) => {
        setProgram(progRes.data);
        // Flatten folder tree to flat list for the selector
        const flat: Folder[] = [];
        const walk = (arr: any[]) => arr.forEach((f: any) => { flat.push({ id: f.id, name: f.name }); walk(f.children ?? []); });
        walk(foldersRes.data);
        setFolders(flat);
      })
      .catch(err => {
        if (err?.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [ident, admin]);

  const handleDelete = async () => {
    if (!program) return;
    try {
      await api.delete(`/programs/${program.id}`);
      toast.success("프로그램 삭제됨");
      nav("/programs");
    } catch { toast.error("삭제 실패"); }
  };

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "var(--text-3)", fontSize: 13 }}>
      불러오는 중…
    </div>
  );

  if (notFound || !program) return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 12 }}>
      <div style={{ fontSize: 48 }}>🔍</div>
      <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text-1)" }}>프로그램을 찾을 수 없습니다</div>
      <button className="t-btn" onClick={() => nav("/programs")}>← 목록으로</button>
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* Header bar — 앱 안에서는 통째로 뺀다 */}
      {!inApp && (
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 24px", borderBottom: "1px solid var(--border)", flexShrink: 0, background: "var(--bg-0)" }}>
        {!bare && (
          <button
            onClick={() => nav(-1)}
            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-3)", display: "flex", alignItems: "center", gap: 4, fontSize: 12, padding: "4px 6px", borderRadius: 5 }}
            onMouseEnter={e => (e.currentTarget.style.background = "var(--bg-2)")}
            onMouseLeave={e => (e.currentTarget.style.background = "none")}
          >
            <I.ChevL size={13}/> 뒤로
          </button>
        )}

        <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <span style={{ fontSize: 18 }}>{pageTypeIcon(program.page_type)}</span>
          <h1 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "var(--text-1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {program.name}
          </h1>
          <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 10, background: "rgba(99,102,241,0.1)", color: "var(--p-600,#4f46e5)", fontWeight: 600, flexShrink: 0 }}>
            {pageTypeLabel(program.page_type)}
          </span>
          {!program.is_active && (
            <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 10, background: "#fef3c7", color: "#d97706", fontWeight: 600, flexShrink: 0 }}>
              비활성
            </span>
          )}
        </div>

        {!bare && (
          <button
            onClick={openLarge}
            title="새 창에서 크게 보기"
            style={{ ...smBtn, display: "flex", alignItems: "center", gap: 5, padding: "5px 12px" }}
            onMouseEnter={e => (e.currentTarget.style.background = "var(--bg-2)")}
            onMouseLeave={e => (e.currentTarget.style.background = "none")}
          >
            ⛶ 크게 보기
          </button>
        )}

        {admin && (
          <button
            onClick={() => setShowAdmin(v => !v)}
            style={{ ...smBtn, display: "flex", alignItems: "center", gap: 5, padding: "5px 12px", background: showAdmin ? "rgba(99,102,241,0.1)" : undefined, color: showAdmin ? "var(--p-600)" : undefined, borderColor: showAdmin ? "var(--p-400)" : undefined }}
          >
            ⚙️ 설정
          </button>
        )}
      </div>
      )}

      {/* Admin settings panel */}
      {admin && showAdmin && !inApp && (
        <AdminSettings program={program} folders={folders} onUpdate={setProgram} onDelete={handleDelete} />
      )}

      {/* Description (when not in admin settings) */}
      {program.description && !showAdmin && !inApp && (
        <div style={{ padding: "10px 24px", borderBottom: "1px solid var(--border)", background: "var(--bg-1)", flexShrink: 0 }}>
          <p style={{ margin: 0, fontSize: 13, color: "var(--text-3)", lineHeight: 1.6 }}>{program.description}</p>
        </div>
      )}

      {/* Content area */}
      {program.page_type === "custom" ? (
        // Custom app fills the area with no padding (iframe / editor manage their own).
        <CustomAppView program={program} admin={admin} />
      ) : program.page_type === "embed" ? (
        // Embedded mini-app — full-bleed same-origin iframe.
        <EmbedView program={program} />
      ) : (
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
          {program.page_type === "catalog"
            ? <CatalogView program={program} admin={admin} onUpdate={setProgram} />
            : <ComingSoon pageType={program.page_type} />}
        </div>
      )}
    </div>
  );
}
